import { Box, Grid } from '@sequencehq/core-components'

import { filterErrorsByField } from 'Automations/automationEditor/utils'
import type { ConditionPropertyEditorComponent } from 'Automations/nodeDefinitions/conditions'
import type { PhaseDurationOperator } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import { DurationValueInput } from '../../../../switch/components/GroupEditor/inputs/DurationValueInput'
import {
  appendPhaseTestId,
  EMPTY_DURATION,
  readPhaseEditorValue,
  usePhaseOperatorState,
  type PhaseEditorValue
} from './PhasePropertyEditor.helpers'
import { PhasePropertyEditorLayout } from './PhasePropertyEditorLayout'
import {
  phaseDurationFromInput,
  phaseDurationToInput
} from './phaseDurationInput'

export const DurationOperatorEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  operators,
  siblingConditions,
  onChange,
  validationErrors,
  focusOnInitialLoad,
  propertyInput,
  removeButton
}) => {
  const { operator: subOperator, duration } = readPhaseEditorValue(value)
  const {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    subOperatorOptions,
    subOperatorDisabled
  } = usePhaseOperatorState({
    operator,
    value,
    operators,
    siblingConditions,
    onChange
  })

  const update = (next: Partial<PhaseEditorValue>) =>
    onChange({
      value: {
        operator: next.operator ?? subOperator,
        duration: next.duration ?? duration ?? EMPTY_DURATION
      }
    })

  const showDuration = Boolean(subOperator && subOperator !== 'IS_OPEN_ENDED')

  return (
    <PhasePropertyEditorLayout
      propertyInput={propertyInput}
      removeButton={removeButton}
      operatorInput={
        <InputSelectControlField
          data-testid={appendPhaseTestId(dataTestId, 'operator')}
          value={operator}
          options={operatorOptions}
          placeholder="Select..."
          disabled={primaryOperatorDisabled}
          onChange={onPrimaryOperatorChange}
          validationErrors={validationErrors?.operator}
          initiallyOpen={focusOnInitialLoad}
        />
      }
      subRow={
        <Grid
          gridTemplateAreas={
            showDuration ? '"subOperator years months days"' : '"subOperator"'
          }
          gridTemplateColumns={
            showDuration
              ? `${SUB_OPERATOR_COLUMN_WIDTH} repeat(3, minmax(0, 1fr))`
              : SUB_OPERATOR_COLUMN_WIDTH
          }
          gap="8px"
        >
          <Box gridArea="subOperator">
            <InputSelectControlField
              data-testid={appendPhaseTestId(dataTestId, 'subOperator')}
              value={subOperator ?? ''}
              options={subOperatorOptions}
              placeholder="Select..."
              disabled={subOperatorDisabled}
              onChange={selected =>
                update({ operator: selected as PhaseDurationOperator })
              }
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'operator'
              )}
            />
          </Box>
          {showDuration && (
            <DurationValueInput
              data-testid={appendPhaseTestId(dataTestId, 'duration')}
              property="PHASE"
              value={phaseDurationToInput(duration ?? EMPTY_DURATION)}
              onChange={({ value: nextDuration }) =>
                update({ duration: phaseDurationFromInput(nextDuration) })
              }
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'duration'
              )}
              context={{ focusOnInitialLoad: false }}
            />
          )}
        </Grid>
      }
    />
  )
}
